import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import TimeAgo from './TimeAgo';

function formatStatus(status: string): string {
  if (!status) return '';
  if (status.toLowerCase() === 'completed') return 'Ready for Review';
  return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function statusIconClass(status: string): string {
  const normalised = status.toLowerCase();
  if (normalised === 'confirmed') return 'bg-emerald-50 text-emerald-600';
  if (normalised === 'discarded') return 'bg-red-50 text-red-600';
  return 'bg-blue-50 text-blue-600';
}

interface IngestionActivityCardProps {
  processId: string;
  fileName: string;
  status: string;
  initiatedAt: Date | string | null | undefined;
  phase?: string | null;
}

/** One ingestion process, as shown in Recent Activity. */
export default function IngestionActivityCard({
  processId,
  fileName,
  status,
  initiatedAt,
  phase,
}: IngestionActivityCardProps) {
  const normalised = status.toLowerCase();
  const showPhase = phase && normalised !== 'confirmed' && normalised !== 'discarded';

  return (
    <Link
      to={`/processing/${processId}`}
      state={{ from: '/upload' }}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group block"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-3 w-full">
          <div className={`p-2.5 rounded-xl flex-shrink-0 ${statusIconClass(status)}`}>
            <FileText className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-gray-900 group-hover:text-emerald-700 transition-colors truncate" title={fileName}>
              {fileName}
            </p>
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <span className="font-medium mr-2">{formatStatus(status)}</span>
              {showPhase && (
                <span className="text-gray-400 truncate max-w-[120px]" title={phase}>
                  {`• ${phase}`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end mt-2 pt-2 border-t border-gray-50">
        <span className="text-xs text-gray-400">
          <TimeAgo date={initiatedAt} />
        </span>
      </div>
    </Link>
  );
}
